import * as React from "react";
import { action } from "mobx";
import { observer } from "mobx-react";

// Material UI
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";

// Models
import {
    CategoryItem,
    LessonCategoryItem,
    ExpressionCategoryItem,
    Languages,
    ImageOverlayType,
    Category,
} from "models";

// Components
import { CategoryItemCardInputBase } from "./CategoryItemCardInputBase";

import { strings } from "../../../core/localizedStrings";

import { StringUtils } from "../../../core/utils/StringUtils";
import { runInAction } from "mobx";

var Row = require("react-flexbox-grid").Row;
var Col = require("react-flexbox-grid").Col;

require("../../../styles/commonStyles.css");

@observer
export class ExpressionCardInput extends CategoryItemCardInputBase {
    constructor(props: any, context: any) {
        super(props, context);

        if (props.categoryItem) {
            if (props.categoryItem instanceof LessonCategoryItem) {
                this.vocabTextFieldValue = props.categoryItem.text ? props.categoryItem.text.trim() : undefined;
                this.useImageAsVocabulary = props.categoryItem.useImageAsVocabularyItem;
            }

            this.questionTextFieldValue = props.categoryItem.question;
            this.answerTextFieldValue = props.categoryItem.answer;
            this.incorrect1Value = props.categoryItem.wrong1;
            this.incorrect2Value = props.categoryItem.wrong2;
            this.incorrect3Value = props.categoryItem.wrong3;

            if (!StringUtils.isEmptyOrUndefined(this.incorrect1Value)) {
                runInAction(() => {
                    this.enterIncorrectAnswers = true;
                });
            }
        }
    }

    // Render UI
    // ------------------------------------------
    render() {
        return (
            <div style={{ padding: 40 }}>
                {this.props.categoryItem &&
                (this.props.categoryItem instanceof ExpressionCategoryItem ||
                    this.props.categoryItem instanceof LessonCategoryItem) ? (
                    <>
                        <Col xs={12}>
                            {this.props.categoryItem.imageDownloadURL && (
                                <Row center="xs">
                                    <img src={this.props.categoryItem.imageDownloadURL} style={{ height: 160 }} />
                                    {this.imageOverlay && this.renderImageOverlayForType(this.imageOverlay)}
                                </Row>
                            )}
                            {this.props.categoryItem instanceof LessonCategoryItem && (
                                <>
                                    <Row center="xs" style={{ paddingLeft: 20 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={this.useImageAsVocabulary}
                                                    onChange={this.onUseImageAsVocabularyItemChanged}
                                                />
                                            }
                                            label={strings.useAsVocabulary}
                                        />
                                    </Row>
                                    {this.useImageAsVocabulary && (
                                        <Row center="xs" style={{ paddingBottom: 20 }}>
                                            <TextField
                                                style={{ width: "60%" }}
                                                id="text"
                                                fullWidth={true}
                                                label={strings.vocabulary}
                                                inputProps={{
                                                    style: {
                                                        textAlign: "center",
                                                        paddingBottom: 10,
                                                        fontSize: 22,
                                                    },
                                                }}
                                                value={this.vocabTextFieldValue}
                                                onChange={this.onTextFieldChange}
                                                onKeyDown={this.onKeyDown}
                                            >
                                                {strings.expressionInputTextField1Label}
                                            </TextField>
                                        </Row>
                                    )}
                                </>
                            )}
                        </Col>
                        <Col xs={12}>{this.renderQuestionAnswerFields()}</Col>
                    </>
                ) : (
                    this.renderQuestionAnswerFields()
                )}

                {this.renderIncorretAnswerRadios()}
                {this.renderIncorrectAnswerTextFields(this.enterIncorrectAnswers)}
                <Row end="xs" style={{ paddingTop: 20 }}>
                    <Button variant="contained" onClick={this.onEditComplete}>
                        {strings.ok}
                    </Button>
                </Row>
            </div>
        );
    }

    protected renderQuestionAnswerFields() {
        let questionLabel = strings.question;
        let answerLabel = strings.answer;
        if (this.props.categoryItem) {
            let currentCategory = this.props.store.rootStore.browseListsStore.itemToEdit as Category;
            if (currentCategory.answersLanguage !== currentCategory.language) {
                questionLabel = Languages.getFriendlyString(currentCategory.voice);
                answerLabel = Languages.getFriendlyString(currentCategory.answersVoice);
            }
        }
        return (
            <>
                <Row>
                    <TextField
                        id="question"
                        inputRef={(ref) => {
                            this.textInputRef = ref;
                        }}
                        fullWidth={true}
                        label={questionLabel}
                        style={{ minWidth: 300 }}
                        inputProps={{ style: { paddingBottom: 10, fontSize: 22 } }}
                        value={this.questionTextFieldValue}
                        onChange={this.onTextFieldChange}
                        onKeyDown={this.onKeyDown}
                    >
                        {strings.expressionInputTextField1Label}
                    </TextField>
                </Row>
                <Row center="xs" style={{ paddingTop: 10 }}>
                    <TextField
                        id="answer"
                        fullWidth={true}
                        label={answerLabel}
                        inputProps={{ style: { paddingTop: 10, fontSize: 22 } }}
                        value={this.answerTextFieldValue}
                        onChange={this.onTextFieldChange}
                        onKeyDown={this.onKeyDown}
                    >
                        {strings.expressionInputTextField2Label}
                    </TextField>
                </Row>
            </>
        );
    }

    protected renderIncorretAnswerRadios() {
        return (
            <>
                <Row style={{ paddingTop: 30 }}>
                    <Typography variant="subtitle1">{strings.multipleChoiceInstructions}</Typography>
                </Row>
                <Row>
                    <FormControl>
                        <RadioGroup
                            name="incorrectAnswers"
                            value={this.enterIncorrectAnswers ? "enterIncorrect" : "useOtherCards"}
                            onChange={this.onEnterIncorrectRadioChanged}
                        >
                            <FormControlLabel
                                style={{ marginTop: -5 }}
                                value="useOtherCards"
                                control={<Radio />}
                                label={strings.useOtherAnswersAsIncorrect}
                            />
                            <FormControlLabel
                                style={{ marginTop: -20 }}
                                value="enterIncorrect"
                                control={<Radio />}
                                label={strings.enterIncorrectAnswers}
                            />
                        </RadioGroup>
                    </FormControl>
                </Row>
            </>
        );
    }

    protected renderIncorrectAnswerTextFields(shouldRender: Boolean) {
        let rowStyle = { paddingTop: 10 };
        if (!shouldRender) {
            return null;
        } else {
            return (
                <div style={{ margin: "-10px 20px 10px 20px" }}>
                    <Row center="xs" style={rowStyle}>
                        <TextField
                            id="incorrect1"
                            inputProps={{ style: { fontSize: 18 } }}
                            fullWidth={true}
                            label="Incorrect Answer 1"
                            value={this.incorrect1Value}
                            onChange={this.onTextFieldChange}
                            onKeyDown={this.onKeyDown}
                        />
                    </Row>
                    <Row center="xs" style={rowStyle}>
                        <TextField
                            id="incorrect2"
                            inputProps={{ style: { fontSize: 18 } }}
                            fullWidth={true}
                            label="Incorrect Answer 2"
                            value={this.incorrect2Value}
                            onChange={this.onTextFieldChange}
                            onKeyDown={this.onKeyDown}
                        />
                    </Row>
                    <Row center="xs" style={rowStyle}>
                        <TextField
                            id="incorrect3"
                            inputProps={{ style: { fontSize: 18 } }}
                            fullWidth={true}
                            label="Incorrect Answer 3"
                            value={this.incorrect3Value}
                            onChange={this.onTextFieldChange}
                            onKeyDown={this.onKeyDown}
                        />
                    </Row>
                </div>
            );
        }
    }

    protected renderImageOverlayForType(imageOverlayType: ImageOverlayType) {
        return null;
        // let style = { height: 160, position: "absolute", opacity: 0.4 } as React.CSSProperties;
        // switch (imageOverlayType) {
        //     case ImageOverlayType.None:
        //         return null;
        //     case ImageOverlayType.Affirmative:
        //         return <img src={ImageOverlays.AFFIRMATIVE} style={style} />;
        //     case ImageOverlayType.Negative:
        //         return <img src={ImageOverlays.NEGATIVE} style={style} />;
        //     default:
        //         break;
        // }
    }

    protected setCategoryItemPropertiesFromTextFields(categoryItem: CategoryItem) {
        if (categoryItem instanceof ExpressionCategoryItem || categoryItem instanceof LessonCategoryItem) {
            if (!StringUtils.isEmptyOrUndefined(this.questionTextFieldValue)) {
                categoryItem.question = this.questionTextFieldValue.trim();
            }

            if (!StringUtils.isEmptyOrUndefined(this.answerTextFieldValue)) {
                categoryItem.answer = this.answerTextFieldValue.trim();
            }

            if (this.enterIncorrectAnswers) {
                if (!StringUtils.isEmptyOrUndefined(this.incorrect1Value)) {
                    categoryItem.wrong1 = this.incorrect1Value.trim();
                } else {
                    categoryItem.wrong1 = null;
                }
                if (!StringUtils.isEmptyOrUndefined(this.incorrect2Value)) {
                    categoryItem.wrong2 = this.incorrect2Value.trim();
                } else {
                    categoryItem.wrong2 = null;
                }
                if (!StringUtils.isEmptyOrUndefined(this.incorrect3Value)) {
                    categoryItem.wrong3 = this.incorrect3Value.trim();
                } else {
                    categoryItem.wrong3 = null;
                }
            } else {
                categoryItem.wrong1 = "";
                categoryItem.wrong2 = "";
                categoryItem.wrong3 = "";
            }

            if (categoryItem instanceof LessonCategoryItem) {
                if (!StringUtils.isEmptyOrUndefined(this.vocabTextFieldValue)) {
                    categoryItem.text = this.vocabTextFieldValue.trim();
                }
                categoryItem.useImageAsVocabularyItem = this.useImageAsVocabulary;
            }

            // if (this.imageOverlay) {
            //     categoryItem.imageOverlay = this.imageOverlay;
            // }
        }
    }

    @action
    private onUseImageAsVocabularyItemChanged = (event: any, value: any) => {
        if (this.props.categoryItem instanceof LessonCategoryItem) {
            this.useImageAsVocabulary = value;
        }
    };

    // @action
    // private onImageOverlaySelected = (overlayType: ImageOverlayType) => {
    //     this.imageOverlay = overlayType;
    // };
}
